import { useState } from 'react'
import { Eye, Sliders, Target, Handshake } from 'lucide-react'
import { AnalysisCard } from './AnalysisCard'

interface ParsedExplanation {
  summary: string
  myra_score: string
  visual_journey: {
    initial_pull_hierarchy: string
    gaze_path_and_flow: string
  }
  emotional_landscape: {
    emotional_state: string
    emotional_and_brand_resonance: string
  }
  socio_political: {
    representation: string
    symbolism_and_archetypes: string
    brand_values_and_associations: string
    implied_narrative_and_dynamics: string
  }
  strategic_refinements: {
    composition_and_lighting_strategy: string
    gaze_fix_suggestions: string
    emotional_and_narrative_shift_suggestions: string
    tempering_graphic_content: string
  }
  questions: string[]
}

interface AnalysisDetailsProps {
  parsedExplanation: ParsedExplanation
}

export const AnalysisDetails = ({
  parsedExplanation,
}: AnalysisDetailsProps) => {
  const [expandedCard, setExpandedCard] = useState<string | null>(null)

  // Combine visual journey content
  const visualContent = `${parsedExplanation.visual_journey.initial_pull_hierarchy}\n\n${parsedExplanation.visual_journey.gaze_path_and_flow}`

  // Combine emotional landscape content
  const emotionalContent = `${parsedExplanation.emotional_landscape.emotional_state}\n\n${parsedExplanation.emotional_landscape.emotional_and_brand_resonance}`

  // Combine socio-political content
  const socioPoliticalContent = `${parsedExplanation.socio_political.representation}\n\n${parsedExplanation.socio_political.symbolism_and_archetypes}`

  // Combine strategic content
  const strategicContent = `${parsedExplanation.strategic_refinements.composition_and_lighting_strategy}\n\n${parsedExplanation.strategic_refinements.gaze_fix_suggestions}`

  const cards = [
    { id: 'visual', icon: Eye, title: 'Visual', content: visualContent },
    {
      id: 'emotional',
      icon: Sliders,
      title: 'Emotional',
      content: emotionalContent,
    },
    {
      id: 'socio-political',
      icon: Handshake,
      title: 'Socio-Political',
      content: socioPoliticalContent,
    },
    {
      id: 'strategic',
      icon: Target,
      title: 'Strategic',
      content: strategicContent,
    },
  ]

  const handleCardClick = (cardId: string) => {
    setExpandedCard(expandedCard === cardId ? null : cardId)
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 transition-all duration-300 ease-in-out mb-8">
      {/* Left side - Heading and Summary */}
      <div className="flex flex-col justify-start lg:col-span-1">
        <h2 className="md:text-5xl text-4xl font-extrabold mb-6 gradient-text leading-tight w-2/3">
          Key Analytical Findings
        </h2>
        <div className="text-sm text-gray-700 leading-relaxed">
          {parsedExplanation.summary}
        </div>
      </div>

      {/* Right side - Grid of cards with overlay */}
      <div className="relative lg:col-span-2">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {cards.map(card => (
            <AnalysisCard
              key={card.id}
              icon={card.icon}
              title={card.title}
              content={card.content}
              isExpanded={false}
              onClick={() => handleCardClick(card.id)}
            />
          ))}
        </div>

        {/* Overlay for expanded card */}
        {expandedCard && (
          <div className="absolute inset-0 z-50 flex items-center justify-center">
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-gray/10 backdrop-blur-md rounded-xl animate-in fade-in-95 duration-300"
              onClick={() => setExpandedCard(null)}
            />
            {/* Expanded card */}
            <div className="relative w-[60%] max-h-full overflow-y-auto shadow-2xl animate-in zoom-in-95 duration-300">
              {cards
                .filter(card => card.id === expandedCard)
                .map(card => (
                  <AnalysisCard
                    key={card.id}
                    icon={card.icon}
                    title={card.title}
                    content={card.content}
                    isExpanded={true}
                    onClick={() => handleCardClick(card.id)}
                  />
                ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
